<template>
  <div class="space-y-4">

    <div v-if="!labTests.length" class="text-sm text-gray-400 py-4">
      No lab tests recorded for this visit.
    </div>

    <div v-for="test in labTests" :key="test.id"
      class="border border-gray-200 rounded-lg overflow-hidden">

      <!-- Test header -->
      <div class="flex items-center justify-between px-4 py-3 bg-gray-50">
        <div>
          <p class="font-medium text-gray-800 text-sm">{{ test.test_type?.name || '—' }}</p>
          <p v-if="test.sample_code" class="text-xs text-gray-500 mt-0.5">Sample: {{ test.sample_code }}</p>
        </div>
        <div class="flex items-center gap-3">
          <span v-if="test.queue_priority"
            class="text-xs px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200">
            Priority: {{ test.queue_priority }}
          </span>
          <span class="text-xs px-2 py-0.5 rounded-full border" :class="statusStyle(test.status?.name)">
            {{ test.status?.name || 'Unknown' }}
          </span>
        </div>
      </div>

      <!-- Results + edit -->
      <div class="px-4 py-3">

        <!-- Viewing existing results (versioned) -->
        <div v-if="editingTestId !== test.id">
          <!-- Structured per-analyte results, when the test was captured that way -->
          <div v-if="test.analyte_results && test.analyte_results.length" class="mb-3 overflow-x-auto">
            <table class="w-full text-sm border border-gray-200 rounded overflow-hidden">
              <thead class="bg-gray-100 text-xs text-gray-500 uppercase">
                <tr>
                  <th class="px-3 py-1.5 text-left">Analyte</th>
                  <th class="px-3 py-1.5 text-left">Value</th>
                  <th class="px-3 py-1.5 text-left">Unit</th>
                  <th class="px-3 py-1.5 text-left">Reference Range</th>
                  <th class="px-3 py-1.5 text-left">Flag</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100 bg-white">
                <tr v-for="ar in test.analyte_results" :key="ar.id">
                  <td class="px-3 py-1.5 font-medium text-gray-800">
                    {{ ar.lab_analyte?.name }}
                    <span v-if="ar.lab_analyte?.abbreviation" class="text-xs text-gray-400 ml-1">({{ ar.lab_analyte.abbreviation }})</span>
                  </td>
                  <td class="px-3 py-1.5" :class="ar.is_abnormal ? 'font-semibold text-red-700' : 'text-gray-800'">{{ ar.value }}</td>
                  <td class="px-3 py-1.5 text-gray-500">{{ ar.unit || '—' }}</td>
                  <td class="px-3 py-1.5 text-gray-500 text-xs">
                    <span v-if="ar.reference_range_low != null">{{ ar.reference_range_low }} – {{ ar.reference_range_high }}</span>
                    <span v-else>—</span>
                  </td>
                  <td class="px-3 py-1.5">
                    <span v-if="ar.flag === 'H'" class="px-2 py-0.5 text-xs font-bold rounded bg-red-100 text-red-700">H</span>
                    <span v-else-if="ar.flag === 'L'" class="px-2 py-0.5 text-xs font-bold rounded bg-blue-100 text-blue-700">L</span>
                    <span v-else-if="ar.flag === 'N'" class="px-2 py-0.5 text-xs font-bold rounded bg-green-100 text-green-700">N</span>
                    <span v-else class="text-gray-400">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-if="latestLabComment(test)" class="text-xs text-gray-500 mt-1.5 italic">{{ latestLabComment(test) }}</p>
          </div>

          <!-- Free-text results (pre-structured-capture tests, or tests with no assigned analytes) -->
          <div v-else-if="test.lab_results && test.lab_results.length" class="space-y-2">
            <div
              v-for="(result, index) in [...test.lab_results].reverse()"
              :key="result.id || index"
              class="border border-gray-100 rounded p-2 bg-gray-50"
            >
              <div class="flex items-center justify-between mb-1">
                <span class="text-xs font-semibold text-blue-600">
                  {{ index === 0 ? 'Latest Entry' : `Amendment ${test.lab_results.length - 1 - index}` }}
                </span>
                <span class="text-xs text-gray-400">
                  {{ result.created_at ? new Date(result.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '' }}
                  <span v-if="result.filled_by"> · {{ result.filled_by.Username }}</span>
                </span>
              </div>
              <p class="text-sm text-gray-800 whitespace-pre-line">{{ result.results || '—' }}</p>
              <p v-if="result.comments" class="text-xs text-gray-500 mt-1 italic">{{ result.comments }}</p>
            </div>
          </div>
          <p v-else class="text-xs text-gray-400">Results pending.</p>

          <!-- Chain of custody -->
          <div v-if="test.lab_results && test.lab_results.length" class="mt-3 p-2 bg-blue-50 rounded border border-blue-100 text-xs space-y-1">
            <p class="font-semibold text-blue-700 mb-1">Chain of Custody</p>
            <div class="flex gap-1">
              <span class="text-gray-500 w-24 shrink-0">Collected by:</span>
              <span class="font-medium text-gray-800">{{ test.collecter?.Username || '—' }}</span>
            </div>
            <div class="flex gap-1">
              <span class="text-gray-500 w-24 shrink-0">Conducted by:</span>
              <span class="font-medium text-gray-800">{{ test.lab_results[0]?.filled_by?.Username || '—' }}</span>
            </div>
            <div class="flex gap-1">
              <span class="text-gray-500 w-24 shrink-0">Reviewed by:</span>
              <span class="font-medium text-gray-800">{{ test.reviewer?.Username || '—' }}</span>
            </div>
          </div>

          <button @click="startEditResult(test)"
            class="mt-3 text-xs px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50">
            {{ test.lab_results?.length ? 'Add Amendment' : 'Add Results' }}
          </button>
        </div>

        <!-- Edit form -->
        <div v-else class="space-y-3">
          <div>
            <label class="text-xs font-semibold text-gray-500 uppercase">Results</label>
            <textarea v-model="resultForm.results" rows="4"
              class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm p-2"
              placeholder="Enter test results..." />
          </div>
          <div>
            <label class="text-xs font-semibold text-gray-500 uppercase">Comments</label>
            <textarea v-model="resultForm.comments" rows="2"
              class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm p-2"
              placeholder="Optional comments..." />
          </div>
          <div class="flex gap-2">
            <button @click="cancelEditResult"
              class="text-xs px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-600">
              Cancel
            </button>
            <button @click="saveResult(test.id)" :disabled="savingResultId === test.id"
              class="text-xs px-3 py-1.5 rounded bg-custom-blue text-white hover:opacity-90 disabled:opacity-50">
              {{ savingResultId === test.id ? 'Saving...' : 'Save Results' }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="test.notes" class="px-4 pb-3 flex flex-col gap-0.5">
        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Notes</p>
        <p class="text-sm text-gray-700">{{ test.notes }}</p>
      </div>
    </div>

  </div>
</template>

<script>
import apiClient from '@/services/api'

export default {
  name: 'LabStageView',

  props: {
    detail: { type: Object, required: true },
  },

  emits: ['detail-updated'],

  data() {
    return {
      editingTestId:  null,
      savingResultId: null,
      resultForm:     { results: '', comments: '' },
    }
  },

  computed: {
    labTests() { return this.detail.lab_tests || [] },
  },

  methods: {
    startEditResult(test) {
      // Always open a blank form — each save creates a new version, not an overwrite.
      this.resultForm    = { results: '', comments: '' }
      this.editingTestId = test.id
    },

    cancelEditResult() {
      this.editingTestId = null
    },

    async saveResult(labTestId) {
      this.savingResultId = labTestId
      try {
        const response = await apiClient.upsertLabResult(labTestId, this.resultForm)
        if (response.success) {
          this.$emit('detail-updated')
          this.editingTestId = null
          this.$toast.success('Lab results saved')
        } else {
          this.$toast.error(response.message || 'Failed to save results')
        }
      } catch {
        this.$toast.error('Failed to save results')
      } finally {
        this.savingResultId = null
      }
    },

    latestLabComment(test) {
      const latest = test.lab_results?.length ? [...test.lab_results].reverse()[0] : null
      return latest?.comments || ''
    },

    statusStyle(statusName) {
      const s = (statusName || '').toLowerCase()
      if (s.includes('complet') || s.includes('done') || s.includes('publish')) return 'bg-green-50 text-green-700 border-green-200'
      if (s.includes('pending') || s.includes('request')) return 'bg-yellow-50 text-yellow-700 border-yellow-200'
      if (s.includes('reject') || s.includes('cancel')) return 'bg-red-50 text-red-700 border-red-200'
      return 'bg-gray-50 text-gray-600 border-gray-200'
    },
  },
}
</script>
